import type { WorkerImage } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Boxes, Hammer, Pencil, Plus, ScrollText, Trash2 } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Confirm } from '@/components/confirm';
import { Field } from '@/components/field';
import { RelativeTime } from '@/components/relative-time';
import { Section } from '@/components/section';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

const EXAMPLE = `RUN apt-get update \\
 && apt-get install -y --no-install-recommends ffmpeg imagemagick \\
 && rm -rf /var/lib/apt/lists/*
RUN pip install --no-cache-dir pandas`;

const building = (i: WorkerImage) => i.build.status === 'queued' || i.build.status === 'building';

/** The account's worker images, refreshed while one is being built. */
export function useWorkerImages(accountId: string | undefined) {
  return useQuery({
    queryKey: qk.workerImages(accountId ?? ''),
    queryFn: () => api.workerImages.list(accountId!),
    enabled: !!accountId,
    refetchInterval: (q) => (q.state.data?.images.some(building) ? 5000 : false),
  });
}

/**
 * Settings → Worker images (docs/features/worker-images.md): the account's own images for agent runs, the Deskworks
 * worker image plus a Dockerfile addition. Account admins make and change them; agents pick one on their edit dialog.
 */
export function WorkerImagesSection({ accountId, admin }: { accountId: string; admin: boolean }) {
  const images = useWorkerImages(accountId);
  const canBuild = admin && images.data?.available;

  return (
    <Section
      icon={<Boxes />}
      title={t('Worker images')}
      description={t(
        'Agents run on the Deskworks worker image (Python, Node, git, curl, jq, the Claude and Codex CLIs). When an agent needs more, make an image of your own: the worker image plus a few Dockerfile lines. Deskworks builds it and rebuilds it with every new Deskworks version. Pick it on the agent.',
      )}
      actions={
        canBuild ? (
          <WorkerImageDialog accountId={accountId}>
            <Button size="sm">
              <Plus /> {t('New image')}
            </Button>
          </WorkerImageDialog>
        ) : undefined
      }
    >
      {images.isPending ? (
        <LoadingRows rows={2} />
      ) : images.isError ? (
        <ErrorState error={images.error} onRetry={() => void images.refetch()} />
      ) : (
        <>
          {!images.data.available && (
            <p className="mb-4 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
              {images.data.unavailableReason === 'Worker images aren’t enabled for this account yet.'
                ? t('Worker images aren’t enabled for this account yet.')
                : t('Image builds aren’t set up on this installation.')}{' '}
              {images.data.images.length > 0 && t('Images already built keep working.')}
            </p>
          )}
          <ul className="-mx-5 -mb-5 divide-y border-t">
            <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3">
              <span className="text-sm font-medium">{t('Standard')}</span>
              <Badge tone="green">{t('Ready')}</Badge>
              <span className="text-xs text-muted-foreground">
                {t('Deskworks worker {version} · every agent without an image of its own', { version: images.data.version })}
              </span>
            </li>
            {images.data.images.map((image) => (
              <WorkerImageRow key={image.id} image={image} accountId={accountId} admin={admin} canBuild={!!canBuild} />
            ))}
          </ul>
          {images.data.images.length === 0 && images.data.available && (
            <EmptyState
              compact
              className="mt-8"
              title={t('No images of your own yet')}
              description={t('E.g. “Media” with ffmpeg and imagemagick, or “Browser” with Playwright and Chromium.')}
            />
          )}
        </>
      )}
    </Section>
  );
}

function WorkerImageRow({ image, accountId, admin, canBuild }: { image: WorkerImage; accountId: string; admin: boolean; canBuild: boolean }) {
  const rebuild = useApiMutation(() => api.workerImages.rebuild(image.id), {
    invalidate: [qk.workerImages(accountId)],
    success: t('Build started'),
  });
  const remove = useApiMutation(() => api.workerImages.remove(image.id), {
    invalidate: [qk.workerImages(accountId)],
    success: t('Image deleted'),
  });
  const n = image.agents.length;

  return (
    <li className="flex flex-wrap items-start gap-3 px-5 py-3 sm:flex-nowrap">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{image.name}</span>
          <StatusBadge image={image} />
          {n > 0 && (
            <span className="text-xs text-muted-foreground" title={image.agents.map((a) => `${a.name} (${a.workspaceName})`).join(', ')}>
              {n === 1 ? t('1 agent') : t('{n} agents', { n })}
            </span>
          )}
        </div>
        {image.description && <p className="mt-0.5 text-xs text-muted-foreground">{image.description}</p>}
        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
          {image.builtAt && (
            <span>
              {t('Built for {version}', { version: image.builtVersion ?? '?' })} · <RelativeTime value={image.builtAt} />
            </span>
          )}
          <span className="truncate font-mono">{firstLine(image.dockerfile)}</span>
        </div>
        {image.build.status === 'failed' && image.build.error && (
          <p className="mt-1 rounded-md bg-red-50 px-2 py-1 text-xs text-red-800 dark:bg-red-950/40 dark:text-red-300">
            {image.build.error}
            {image.imageRef && ` ${t('Runs keep using the last good build.')}`}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-1 sm:shrink-0">
        <BuildLogDialog image={image}>
          <Button variant="ghost" size="sm">
            <ScrollText /> {t('Build log')}
          </Button>
        </BuildLogDialog>
        {admin && (
          <>
            {canBuild && !building(image) && (
              <Button variant="ghost" size="sm" onClick={() => rebuild.mutate()} disabled={rebuild.isPending} title={t('Build again on the current worker image, e.g. for newer packages')}>
                <Hammer /> {t('Rebuild')}
              </Button>
            )}
            <WorkerImageDialog accountId={accountId} image={image} canBuild={canBuild}>
              <Button variant="ghost" size="sm">
                <Pencil /> {t('Edit')}
              </Button>
            </WorkerImageDialog>
            <Confirm
              destructive
              title={t('Delete “{name}”?', { name: image.name })}
              description={
                n > 0
                  ? t('{n} agents use this image. They run on the standard image again.', { n })
                  : t('No agent uses this image.')
              }
              confirmLabel={t('Delete')}
              onConfirm={() => remove.mutateAsync()}
            >
              <Button variant="ghost" size="sm" aria-label={t('Delete')}>
                <Trash2 />
              </Button>
            </Confirm>
          </>
        )}
      </div>
    </li>
  );
}

function StatusBadge({ image }: { image: WorkerImage }) {
  if (image.status === 'building') return <Badge tone="blue">{image.build.status === 'queued' ? t('Waiting to build') : t('Building …')}</Badge>;
  if (image.status === 'failed') return <Badge tone="red">{t('Build failed')}</Badge>;
  if (building(image)) return <Badge tone="blue" title={t('Runs use the last good build until the new one is ready')}>{t('Ready · rebuilding …')}</Badge>;
  if (image.outdated && image.build.status === 'failed') return <Badge tone="amber">{t('Ready · last build failed')}</Badge>;
  return <Badge tone="green">{t('Ready')}</Badge>;
}

const firstLine = (text: string) => text.split('\n').find((l) => l.trim() && !l.trim().startsWith('#'))?.trim() ?? '';

/** Create (no `image`) or change a worker image; a changed addition builds it again. */
function WorkerImageDialog({ accountId, image, canBuild = true, children }: { accountId: string; image?: WorkerImage; canBuild?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dockerfile, setDockerfile] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(image?.name ?? '');
    setDescription(image?.description ?? '');
    setDockerfile(image?.dockerfile ?? '');
  }, [open, image]);

  const changedAddition = !!image && dockerfile !== image.dockerfile;
  const save = useApiMutation(
    () =>
      image
        ? api.workerImages.update(image.id, { name: name.trim(), description: description.trim(), ...(changedAddition ? { dockerfile } : {}) })
        : api.workerImages.create(accountId, { name: name.trim(), description: description.trim(), dockerfile }),
    {
      invalidate: [qk.workerImages(accountId)],
      success: image ? (changedAddition ? t('Saved – the image is being built again') : t('Image saved')) : t('Image created – the build has started'),
      onSuccess: () => setOpen(false),
    },
  );

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() && dockerfile.trim()) save.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-2xl">
        <form onSubmit={onSubmit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{image ? t('Edit worker image') : t('New worker image')}</DialogTitle>
            <DialogDescription>
              {t('Deskworks puts your lines below FROM <worker image> and runs them as root. After them the image is the worker again, with its user and start command.')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('Name')}>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('e.g. Media')} autoFocus required maxLength={80} />
            </Field>
            <Field label={t('Description')} optional>
              <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('e.g. ffmpeg and imagemagick')} />
            </Field>
          </div>
          <Field
            label={t('Dockerfile addition')}
            hint={t(
              'RUN, ENV, ARG, LABEL, WORKDIR, USER, ADD, COPY --from=<image> and SHELL. Install into /usr/local or /opt: /work, /tmp and the home folder are empty when a run starts. No secrets here: use Secrets for those.',
            )}
          >
            <Textarea
              value={dockerfile}
              onChange={(e) => setDockerfile(e.target.value)}
              rows={10}
              placeholder={EXAMPLE}
              spellCheck={false}
              className="font-mono text-[13px] leading-relaxed"
              disabled={!canBuild}
            />
          </Field>
          {changedAddition && (
            <p className="-mt-2 text-xs text-muted-foreground">{t('Saving builds the image again. Runs keep using the last good build until the new one is ready.')}</p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!name.trim() || !dockerfile.trim()}>
              {image ? t('Save') : t('Create and build')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** The latest build's output, live while it runs. */
function BuildLogDialog({ image, children }: { image: WorkerImage; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const log = useQuery({
    queryKey: qk.workerImageLog(image.id),
    queryFn: () => api.workerImages.buildLog(image.id),
    enabled: open,
    refetchInterval: (q) => (q.state.data && (q.state.data.status === 'queued' || q.state.data.status === 'building') ? 3000 : false),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('Build log · {name}', { name: image.name })}</DialogTitle>
          <DialogDescription>
            {image.build.startedAt ? (
              <>
                {t('Started')} <RelativeTime value={image.build.startedAt} />
                {image.build.version && ` · ${t('worker {version}', { version: image.build.version })}`}
              </>
            ) : (
              t('Waiting for a free build slot.')
            )}
          </DialogDescription>
        </DialogHeader>
        {log.isPending ? (
          <LoadingRows rows={4} />
        ) : log.isError ? (
          <ErrorState error={log.error} onRetry={() => void log.refetch()} />
        ) : (
          <pre className="max-h-[60vh] overflow-auto rounded-md bg-muted p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap break-all">
            {log.data.log || (log.data.status === 'queued' ? t('Waiting for a free build slot.') : t('No output.'))}
          </pre>
        )}
      </DialogContent>
    </Dialog>
  );
}
